import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaShareAlt, FaFacebook, FaWhatsapp, FaTwitter, FaLink, FaEye } from "react-icons/fa";
import fallbackImage1 from "../assets/fallback1.webp";
import fallbackImage2 from "../assets/fallback2.webp";
import heroImage from "../assets/hero.webp";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageUrls, setImageUrls] = useState({});
  const [showShare, setShowShare] = useState({});
  const blogsPerPage = 9;

  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const findValidImageUrl = async (postId) => {
    const url = `https://www.pcds.co.in/cpm/imagesarticle/b${postId}.png`;
    const exists = await checkImageExists(url);
    return exists ? url : (Math.random() > 0.5 ? fallbackImage1 : fallbackImage2);
  };

  const getBlogUrl = (blog) => `/article/${blog.url}.php`;

  const toggleShare = (id) => {
    setShowShare((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const shareBlog = (platform, blog) => {
    const blogUrl = `${window.location.origin}${getBlogUrl(blog)}`;
    const shareText = `Check out this blog: ${blog.title}`;
    const ogImageUrl = imageUrls[blog.id] || heroImage;

    switch (platform) {
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(blogUrl)}&picture=${encodeURIComponent(ogImageUrl)}`);
        break;
      case "whatsapp":
        window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${blogUrl}`)}`);
        break;
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(blogUrl)}`);
        break;
      case "copy":
        navigator.clipboard.writeText(blogUrl).then(() => {
          alert("Link copied to clipboard!");
        });
        break;
      default:
        break;
    }

    setShowShare({});
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch("https://www.pcds.co.in/blogapi.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            uri: "get",
            website: "https://www.unnatkheti.org",
            page: 1
          }),
        });
        const data = await res.json();

        const processed = data.map((blog) => ({
          ...blog,
          title: blog.title || "Untitled Article",
          excerpt: blog.body?.replace(/<[^>]*>?/gm, '').substring(0, 100) + '...' || 'Read more...',
          date: blog.datec
            ? new Date(blog.datec).toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "Unknown date",
          viewCount: blog.viewcount || 0,
          keywords: blog.keywords || "",
          created_at: blog.created_at || new Date().toISOString(),
          url: blog.url || blog.id.toString(),
          seoTitle: blog.title ? `${blog.title} | कृषि ब्लॉग` : "कृषि ब्लॉग | नवीनतम कृषि समाचार और टिप्स",
          seoDescription: blog.body 
            ? blog.body.replace(/<[^>]+>/g, '').substring(0, 160) 
            : "हिंदी में नवीनतम कृषि समाचार, उपाय और टिप्स पाएं। किसानों के लिए उपयोगी जानकारी और आधुनिक कृषि तकनीकें।",
          seoKeywords: blog.keywords || "कृषि, खेती, किसान, कृषि तकनीक, कृषि समाचार"
        }));

        setBlogs(processed);

        const imgObj = {};
        const firstBlogs = processed.slice(0, blogsPerPage);
        for (const blog of firstBlogs) {
          imgObj[blog.id] = await findValidImageUrl(blog.id);
        }
        setImageUrls(imgObj);
      } catch (e) {
        console.error("Error:", e);
      }
    };

    fetchBlogs();
  }, []);

  useEffect(() => {
    const preloadImages = async () => {
      const start = (currentPage - 1) * blogsPerPage;
      const currentBlogs = blogs.slice(start, start + blogsPerPage);

      const imgObj = { ...imageUrls };
      for (const blog of currentBlogs) {
        if (!imgObj[blog.id]) {
          imgObj[blog.id] = await findValidImageUrl(blog.id);
        }
      }
      setImageUrls(imgObj);
    };

    if (blogs.length) preloadImages();
  }, [currentPage, blogs]);

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(blogs.length / blogsPerPage);
  const paginate = (page) => setCurrentPage(page);

  const formatViewCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  return (
    <div className="bg-[#f2fff3] min-h-screen mt-20">
      {/* Dynamic SEO and OG tags */}
      <Helmet>
        <title>कृषि ब्लॉग | नवीनतम कृषि समाचार और टिप्स</title>
        <meta
          name="description"
          content="हिंदी में नवीनतम कृषि समाचार, उपाय और टिप्स पाएं। किसानों के लिए उपयोगी जानकारी और आधुनिक कृषि तकनीकें।"
        />
        <meta 
          name="keywords" 
          content={blogs.slice(0, 5).map(blog => blog.keywords).filter(Boolean).join(', ')} 
        />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content="कृषि ब्लॉग | नवीनतम कृषि समाचार और टिप्स" />
        <meta property="og:description" content="हिंदी में नवीनतम कृषि समाचार, उपाय और टिप्स पाएं। किसानों के लिए उपयोगी जानकारी और आधुनिक कृषि तकनीकें।" />
        <meta property="og:image" content={heroImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="कृषि ब्लॉग" />
        <meta property="og:locale" content="hi_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="कृषि ब्लॉग | नवीनतम कृषि समाचार और टिप्स" />
        <meta name="twitter:description" content="हिंदी में नवीनतम कृषि समाचार, उपाय और टिप्स पाएं। किसानों के लिए उपयोगी जानकारी और आधुनिक कृषि तकनीकें।" />
        <meta name="twitter:image" content={heroImage} />
        
        {/* Canonical */}
        <link rel="canonical" href={window.location.href} />
        
        {/* Article structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": blogs.slice(0, 5).map((blog, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Article",
                "url": `${window.location.origin}${getBlogUrl(blog)}`,
                "name": blog.title,
                "description": blog.excerpt,
                "datePublished": blog.created_at,
                "image": imageUrls[blog.id] || heroImage,
                "author": {
                  "@type": "Person",
                  "name": blog.username || "Unknown Author"
                }
              }
            }))
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div
        className="w-full h-80 bg-cover bg-center flex items-center justify-center relative"
        style={{
          backgroundImage: `url(${heroImage})`,
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="text-white text-center relative z-10 px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">कृषि ब्लॉग</h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            नवीनतम कृषि समाचार, उपाय और टिप्स पाएं हिंदी में
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Blog Cards */}
          <div className="lg:col-span-3">
            <h2 className="text-2xl font-bold text-green-800 mb-6 border-b pb-2">
              Latest Articles
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentBlogs.map((blog) => (
                <div
                  key={blog.id}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 relative"
                >
                  {/* Individual article meta tags */}
                  <Helmet>
                    <meta property="og:title" content={blog.seoTitle} />
                    <meta property="og:description" content={blog.seoDescription} />
                    <meta property="og:image" content={imageUrls[blog.id] || heroImage} />
                    <meta name="twitter:title" content={blog.seoTitle} />
                    <meta name="twitter:description" content={blog.seoDescription} />
                    <meta name="twitter:image" content={imageUrls[blog.id] || heroImage} />
                  </Helmet>

                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={imageUrls[blog.id] || fallbackImage1}
                      alt={blog.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = Math.random() > 0.5 ? fallbackImage1 : fallbackImage2;
                      }}
                    />
                    <button
                      onClick={() => toggleShare(blog.id)}
                      className="absolute top-2 right-2 bg-white p-2 rounded-full shadow hover:bg-gray-100 transition-colors"
                      aria-label="Share"
                    >
                      <FaShareAlt className="text-gray-700" />
                    </button>
                    {showShare[blog.id] && (
                      <div className="absolute top-12 right-2 bg-white rounded shadow-lg p-2 flex flex-col space-y-2 z-10 animate-fadeIn">
                        <button
                          onClick={() => shareBlog("facebook", blog)}
                          className="flex items-center text-blue-600 text-sm hover:underline px-2 py-1 rounded hover:bg-blue-50"
                        >
                          <FaFacebook className="mr-2" /> Facebook
                        </button>
                        <button
                          onClick={() => shareBlog("whatsapp", blog)}
                          className="flex items-center text-green-600 text-sm hover:underline px-2 py-1 rounded hover:bg-green-50"
                        >
                          <FaWhatsapp className="mr-2" /> WhatsApp
                        </button>
                        <button
                          onClick={() => shareBlog("twitter", blog)}
                          className="flex items-center text-blue-400 text-sm hover:underline px-2 py-1 rounded hover:bg-blue-50"
                        >
                          <FaTwitter className="mr-2" /> Twitter
                        </button>
                        <button
                          onClick={() => shareBlog("copy", blog)}
                          className="flex items-center text-gray-700 text-sm hover:underline px-2 py-1 rounded hover:bg-gray-50"
                        >
                          <FaLink className="mr-2" /> Copy Link
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col h-64">
                    <p className="text-xs text-green-600 mb-1">{formatDate(blog.created_at)}</p>
                    <h3 className="font-semibold text-base text-gray-800 mb-2 line-clamp-2">
                      {blog.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3" title={blog.excerpt}>
                      {blog.excerpt}
                    </p>
                    <div className="flex justify-between text-xs text-gray-600 mb-3 mt-auto">
                      <span>By {blog.username}</span>
                      <span className="flex items-center">
                        <FaEye className="mr-1 text-gray-500" />{" "}
                        {formatViewCount(blog.viewCount)} views
                      </span>
                    </div>
                    <a
                      href={getBlogUrl(blog)}
                      className="mt-2 bg-green-600 hover:bg-green-700 text-white py-2 text-sm text-center rounded transition-colors duration-300"
                    >
                      Read More
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-10 space-x-2">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                  aria-label="Previous page"
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-4 py-2 rounded transition-colors ${
                        currentPage === pageNum
                          ? "bg-green-600 text-white"
                          : "border text-gray-600 hover:bg-gray-100"
                      }`}
                      aria-label={`Page ${pageNum}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                  aria-label="Next page"
                >
                  &gt;
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-xl font-bold border-b pb-2 mb-4 text-green-800">
                Recent Posts
              </h3>
              <div className="space-y-4">
                {blogs.slice(0, 5).map((blog) => (
                  <div key={`sidebar-${blog.id}`} className="flex gap-3">
                    <img
                      src={imageUrls[blog.id] || fallbackImage2}
                      alt="thumb"
                      className="w-16 h-16 object-cover rounded"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = Math.random() > 0.5 ? fallbackImage1 : fallbackImage2;
                      }}
                    />
                    <div>
                      <a
                        href={getBlogUrl(blog)}
                        className="text-sm font-medium text-green-800 hover:underline line-clamp-2"
                        title={blog.excerpt}
                      >
                        {blog.title}
                      </a>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(blog.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Blog;